import _ from 'lodash';

import { StationTypes, DelayRepayStationTypes } from '../constants/AppConstants';
import APIClient, { NotFoundError } from '../lib/APIClient';
import Station from '../records/Station';
import TicketOffice from '../records/TicketOffice';
import { resolve, resolveAll } from '../helpers/LinkMapper';
import AuthenticatedAPIClient from '../lib/AuthenticatedAPIClient';

const dataUrl = '/data/stations';
const configUrl = '/config/stations';
const stationCache = {};

function stationConfigResolver (stationConfig, station) {
    if (!station) {
        station = resolve(stationConfig.station).with(Object);
    }

    station.validity = Object.assign(
        {},
        stationConfig.default,
        { DaySaveOrigin: stationConfig.daySaveOrigin },
        _.mapKeys(stationConfig.season, (x, type) => `season${_.upperFirst(type)}`),
        _.mapKeys(stationConfig.smartcard, (x, type) => `smartcard${_.upperFirst(type)}`)
    );
    station.id = stationConfig.station;
    station.onwardTaxiBookingAvailable = stationConfig.onwardTaxiBookingAvailable;
    station.displayInfo = stationConfig.displayInfo || {};

    return resolve(station).as(Station);
}

const StationService = {
    search (term, stationType, filterByBrand = false) {
        if (stationType === 'TicketOnDeparture' || stationType === 'SmartcardCollection') {
            console.warn(`${stationType} station types not yet supported, defaulting to Origin`);
            stationType = StationTypes.ORIGIN;
        }

        const filterByBrandParam = filterByBrand ? { filterByBrand: true } : null;

        const params = {
            search: term,
            ...filterByBrandParam
        };

        if (stationType) {
            if (Object.values(DelayRepayStationTypes).includes(stationType)) {
                params.type = stationType.replace(/^delayRepay/, '');
                params.delayrepay = true;
            } else {
                params.type = _.upperFirst(stationType);
            }
        }

        return APIClient.get(configUrl, params)
            .then((response) => resolveAll(response.result).with(stationConfigResolver));
    },

    get (nlc) {
        if (stationCache[nlc]) {
            return stationCache[nlc];
        }

        stationCache[nlc] = APIClient.get(dataUrl + '/' + nlc)
            .then((response) => {
                if (response.result) {
                    return resolve({ ...response.result, id: dataUrl + '/' + nlc}).as(Station);
                }
                return new Station();
            })
            .catch((error) => {
                if (error instanceof NotFoundError) { return new Station(); }
                delete stationCache[nlc];
                throw error;
            });

        return stationCache[nlc];
    },

    findNearest (latitude, longitude, numStations = 5) {
        return APIClient.get(configUrl, { latitude, longitude, num_stations: numStations }, {loader: true}) // eslint-disable-line camelcase
            .then((response) => resolveAll(response.result).with(stationConfigResolver));
    },

    async getTicketOfficeDetails (station) {
        try {
            const response = await APIClient.get(`/data/ticket-offices/${station.crs}`);
            return resolve(response.result).as(TicketOffice);
        } catch (e) {
            if (e instanceof NotFoundError) {
                console.warn(`Ticket office not found for ${station.crs}`, e.message);
                return null;
            }
            throw e;
        }
    },

    getStations(nlc) {
        if (stationCache[nlc]) {
            return stationCache[nlc];
        }

        stationCache[nlc] = AuthenticatedAPIClient.get(configUrl + '?search=' + nlc)
            .then(response => response.result || null)
            .catch(error => {
                throw error;
            });

        return stationCache[nlc];
    },
};

export default StationService;
